import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { MdCampaign, MdDelete, MdEdit } from 'react-icons/md';
import Card from 'components/card';
import announcementServices from 'ApiService/Common/announcementServices';
import AnnouncementForm from './AnnouncementForm';
import ConfirmDialog from './ConfirmDialog';
import { AudienceChip, formatAnnouncementDate, postedByLabel } from './helpers';

const Index = () => {
  const [targets, setTargets] = useState({
    scopes: [],
    branches: [],
    batches: [],
  });
  const [editing, setEditing] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const formRef = useRef(null);

  const fetchAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      const response = await announcementServices.list({ limit: 50 });
      if (response.data.success) {
        setAnnouncements(response.data.data);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch announcements'
      );
    }
    setLoading(false);
  }, []);

  const fetchTargets = useCallback(async () => {
    try {
      const response = await announcementServices.getTargets();
      if (response.data.success) {
        setTargets(response.data.data);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to load announcement options'
      );
    }
  }, []);

  useEffect(() => {
    fetchTargets();
    fetchAnnouncements();
  }, [fetchTargets, fetchAnnouncements]);

  const handleEdit = (announcement) => {
    setEditing(announcement);
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSaved = (saved, wasEditing) => {
    if (wasEditing) {
      setAnnouncements((prev) =>
        prev.map((item) => (item._id === saved._id ? saved : item))
      );
      setEditing(null);
    } else {
      fetchAnnouncements();
    }
  };

  const handleDelete = async (id) => {
    setDeleting(true);
    try {
      const response = await announcementServices.remove(id);
      if (response.data.success) {
        toast.success(response.data.message);
        setAnnouncements((prev) => prev.filter((item) => item._id !== id));
        if (editing?._id === id) setEditing(null);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to delete announcement'
      );
    }
    setDeleting(false);
    setConfirmingDelete(null);
  };

  return (
    <div className="mt-3 flex h-full w-full flex-col gap-[20px] rounded-[20px]">
      <Card extra="w-full p-6">
        <div ref={formRef} className="mb-4 flex items-center gap-2">
          <MdCampaign className="h-6 w-6 text-blue-500" />
          <h4 className="text-lg font-semibold text-navy-700 dark:text-white">
            {editing ? 'Edit Announcement' : 'Create Announcement'}
          </h4>
        </div>

        <AnnouncementForm
          editing={editing}
          targets={targets}
          onSaved={handleSaved}
          onCancel={() => setEditing(null)}
        />
      </Card>

      <Card extra="w-full p-6">
        <h4 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">
          Announcements
        </h4>

        {loading ? (
          <div className="flex justify-center py-10">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500" />
          </div>
        ) : announcements.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">
            No announcements yet.
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            {announcements.map((announcement) => (
              <div
                key={announcement._id}
                className={`rounded-xl border p-4 ${
                  editing?._id === announcement._id
                    ? 'border-blue-400 dark:border-blue-400'
                    : 'border-gray-200 dark:border-white/10'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h5 className="text-base font-semibold text-navy-700 dark:text-white">
                      {announcement.title}
                    </h5>
                    <p className="mt-1 whitespace-pre-line text-sm text-gray-600 dark:text-gray-300">
                      {announcement.message}
                    </p>
                  </div>
                  {/* canEdit / canDelete come from the API, so the rule lives in one place. */}
                  <div className="flex shrink-0 items-center gap-1">
                    {announcement.canEdit && (
                      <button
                        type="button"
                        onClick={() => handleEdit(announcement)}
                        title="Edit announcement"
                        className="rounded-lg p-2 text-blue-500 hover:bg-blue-50 dark:hover:bg-navy-700"
                      >
                        <MdEdit className="h-5 w-5" />
                      </button>
                    )}
                    {announcement.canDelete && (
                      <button
                        type="button"
                        onClick={() => setConfirmingDelete(announcement)}
                        title="Delete announcement"
                        className="rounded-lg p-2 text-red-500 hover:bg-red-50 dark:hover:bg-navy-700"
                      >
                        <MdDelete className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                  <AudienceChip announcement={announcement} />
                  <span>{postedByLabel(announcement)}</span>
                  <span>{formatAnnouncementDate(announcement.createdAt)}</span>
                  {announcement.updatedAt &&
                    announcement.updatedAt !== announcement.createdAt && (
                      <span className="italic">
                        edited {formatAnnouncementDate(announcement.updatedAt)}
                      </span>
                    )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {confirmingDelete && (
        <ConfirmDialog
          title="Delete this announcement?"
          message={`"${confirmingDelete.title}" will no longer be visible to anyone. This cannot be undone.`}
          busy={deleting}
          onConfirm={() => handleDelete(confirmingDelete._id)}
          onCancel={() => setConfirmingDelete(null)}
        />
      )}
    </div>
  );
};

export default Index;
